import { useId, useState } from 'react'

import classNames from 'classnames'
import { useTranslation } from 'react-i18next'
import { connect, DataItem } from 'scrivito'

import { AutomationEventActionMenu } from './AutomationEventActionMenu'
import jrIcon from '@/assets/images/jr-icon.svg'
import { Icon } from '@/Atomic/Atoms/Icon'
import { MenuItemProps } from '@/Atomic/Molecules/MenuItem'
import { TimelineItemHeader } from '@/Atomic/Molecules/TimelineItemHeader'
import { SublistItem } from '@/Atomic/Templates/Sublist'
import { TimelineItem } from '@/Atomic/Templates/TimelineItem'
import { getActivityTypeIcon } from '@/Components/Activity/getActivityTypeIcon'
import { ConfirmationModal } from '@/Components/shared/modals/ConfirmationModal'
import { errorToast } from '@/Components/Toast'
import { DataClassName } from '@/Data/const'
import { useDoubleClick } from '@/hooks/useDoubleClick'
import { useModalHandler } from '@/hooks/useModalHandler'
import {
  getAutomationsLink,
  navigateToAutomations,
} from '@/utils/automationsUtils'
import { getAttributeDisplayValue } from '@/utils/CRUD/read/displayValue'
import { getSchemaAttributes } from '@/utils/CRUD/read/schema'
import { getSchemaName } from '@/utils/dataClassMap'
import { formatDuration } from '@/utils/formatDuration'
import { formatTimeAgo } from '@/utils/formatTimeAgo'
import { reportError } from '@/utils/reportError'
import { parseDateAttribute } from '@/utils/timelineDateGroups'
import { getDeleteModalSubtitle } from '@/Widgets/DXPDetails/utils/deleteModalSubtitle'
import { getDeleteModalTitle } from '@/Widgets/DXPDetails/utils/deleteModalTitle'

import './AutomationEventSublistItem.scss'

interface AutomationEventSublistItemProps {
  activities: DataItem[]
}

export const AutomationEventSublistItem: React.FC<AutomationEventSublistItemProps> =
  connect(({ activities }) => {
    const { t } = useTranslation()
    const [isExpanded, setIsExpanded] = useState(false)
    const [isMenuOpen, setIsMenuOpen] = useState(false)
    const [menuTrigger, setMenuTrigger] = useState<boolean>()
    const {
      isOpen: isDeleteModalOpen,
      openModal: openDeleteModal,
      closeModal: closeDeleteModal,
    } = useModalHandler()
    const detailsId = useId()
    const primaryActivity = activities[0]

    const openAutomations = () => {
      if (primaryActivity) navigateToAutomations(primaryActivity)
    }
    const openAutomationsInNewTab = () => {
      if (primaryActivity) navigateToAutomations(primaryActivity, true)
    }
    const deleteActivities = async () => {
      try {
        await Promise.all(activities.map((activity) => activity.delete()))
        closeDeleteModal()
      } catch (error) {
        errorToast(error as Error)
        reportError(error)
      }
    }
    const handleCardClick = useDoubleClick<HTMLAnchorElement>(
      openAutomations,
      openAutomationsInNewTab,
    )

    if (!primaryActivity) return null

    const isGrouped = activities.length > 1
    const title = getActivityTitle(primaryActivity)
    const automationsLink = getAutomationsLink(primaryActivity)
    const eventLabels = activities.map(getAutomationEventLabel)
    const goToActivityPageMenuItem: MenuItemProps = {
      icon: 'activity',
      label: t('common.goToDetailsPage.activity'),
      onClick: openAutomations,
    }
    const deleteMenuItem: MenuItemProps = {
      icon: 'bin',
      label: t('common.delete'),
      onClick: openDeleteModal,
      type: 'danger',
    }
    const menuItems = [goToActivityPageMenuItem, deleteMenuItem]

    const headerAction = isGrouped ? (
      <>
        {t('components.activity.automations.interactions', {
          count: activities.length,
        })}
        <span className="automation-event-sublist-item__range">
          {getGroupRange(activities, t)}
        </span>
        <button
          type="button"
          className="automation-event-sublist-item__details-toggle"
          aria-controls={detailsId}
          aria-expanded={isExpanded}
          onClick={() => setIsExpanded((current) => !current)}
        >
          <Icon
            className={classNames(
              'automation-event-sublist-item__details-icon',
              { 'is-expanded': isExpanded },
            )}
            icon="chevron-down"
            size="sm"
          />
          <span>{t('common.details')}</span>
        </button>
      </>
    ) : (
      eventLabels[0]
    )

    return (
      <>
        <SublistItem className="automation-event-sublist-item">
          <TimelineItem
            icon={getActivityTypeIcon(primaryActivity.get('type'))}
            header={
              <TimelineItemHeader
                className="automation-event-sublist-item__header"
                action={headerAction}
                changedAt={
                  isGrouped
                    ? undefined
                    : getAutomationEventTime(primaryActivity)
                }
                actorAvatar={jrIcon}
              />
            }
          >
            {(!isGrouped || isExpanded) && (
              <div
                className={classNames('automation-event-sublist-item__card', {
                  'automation-event-sublist-item__card--menu-open': isMenuOpen,
                  'automation-event-sublist-item__card--grouped': isGrouped,
                })}
                id={isGrouped ? detailsId : undefined}
                onContextMenu={(event) => {
                  event.preventDefault()
                  event.stopPropagation()
                  setMenuTrigger((currentTrigger) => !currentTrigger)
                }}
              >
                <a
                  className="automation-event-sublist-item__card-link"
                  href={automationsLink}
                  onClick={(event) => {
                    if (
                      event.metaKey ||
                      event.ctrlKey ||
                      event.shiftKey ||
                      event.altKey ||
                      event.button !== 0
                    ) {
                      return
                    }

                    event.preventDefault()
                    handleCardClick(event)
                  }}
                >
                  <span className="automation-event-sublist-item__title">
                    {title}
                  </span>
                  {isGrouped && (
                    <div className="automation-event-sublist-item__events">
                      {activities.map((activity, index) => (
                        <div
                          className="automation-event-sublist-item__event"
                          key={activity.id()}
                        >
                          <span className="automation-event-sublist-item__event-label">
                            {eventLabels[index]}
                          </span>
                          <span className="automation-event-sublist-item__event-time">
                            {formatTimeAgo(getAutomationEventTime(activity))}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </a>
                <div
                  className="automation-event-sublist-item__actions"
                  onClick={(event) => event.stopPropagation()}
                >
                  <AutomationEventActionMenu
                    menuItems={menuItems}
                    onOpenChange={setIsMenuOpen}
                    trigger={menuTrigger}
                  />
                </div>
              </div>
            )}
          </TimelineItem>
        </SublistItem>
        <ConfirmationModal
          variant="danger"
          isOpen={isDeleteModalOpen}
          close={closeDeleteModal}
          confirm={deleteActivities}
          title={t(getDeleteModalTitle(DataClassName.ACTIVITY))}
          subtitle={t(getDeleteModalSubtitle(DataClassName.ACTIVITY))}
        />
      </>
    )
  })

const getActivityTitle = (activity: DataItem): string =>
  (activity.get('displayTitle') as string) ||
  (activity.get('subject') as string) ||
  ''

const getAutomationEventTime = (activity: DataItem): Date | undefined =>
  parseDateAttribute(activity.get('updatedAt') ?? activity.get('createdAt'))

const getAutomationEventLabel = (activity: DataItem): string => {
  const { schemaAttributes } = getSchemaAttributes(
    getSchemaName(DataClassName.ACTIVITY),
  )

  return (
    (getAttributeDisplayValue(
      schemaAttributes?.['subType'],
      activity.get('subType'),
    ) as string) ||
    (activity.get('subType') as string) ||
    ''
  )
}

const getGroupRange = (
  activities: DataItem[],
  t: ReturnType<typeof useTranslation>['t'],
): string => {
  const eventDates = activities
    .map(getAutomationEventTime)
    .filter((date): date is Date => !!date)

  if (!eventDates.length) return ''

  const oldestEventDate = new Date(
    Math.min(...eventDates.map((date) => date.getTime())),
  )
  const distance = formatDuration(oldestEventDate, new Date(), 1)
  return distance
    ? t('components.activity.automations.inLast', { distance })
    : ''
}
